import { useOutletContext, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { buildPath } from "@/lib/routes";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Stagger, StaggerItem } from "@/components/motion/Reveal";
import LeadMagnet from "@/components/LeadMagnet";
import { breadcrumbSchema } from "@/lib/schema";

export default function ProjectsHub() {
  const { lang, t } = useLang();
  const { profile } = useOutletContext();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: api.getProjects });

  const meta = {
    da: { title: "Projekter | Magnus Løv Schmidt", h: ["Projekter jeg", "bygger og driver"], d: "Ved siden af mit SEO-arbejde er jeg involveret i flere projekter — fra linkbuilding og ecommerce til det kreative. Her er dem, jeg brænder mest for." },
    en: { title: "Projects | Magnus Løv Schmidt", h: ["Projects I", "build and run"], d: "Alongside my SEO work I'm involved in several projects — from link building and ecommerce to the creative. Here are the ones I'm most passionate about." },
  }[lang];

  return (
    <>
      <Seo routeKey="projects" lang={lang} title={meta.title} description={meta.d}
        jsonLd={[breadcrumbSchema([{ name: t("nav_home"), path: buildPath("home", lang) }, { name: t("nav_projects"), path: buildPath("projects", lang) }])]} />
      <PageHero title={meta.h} description={meta.d}
        crumbs={[{ label: t("nav_home"), to: "home" }, { label: t("nav_projects") }]} />

      <section data-testid="hub-intro" className="px-6 md:px-12 lg:px-20 pt-12 -mb-4">
        <div className="max-w-3xl space-y-4 text-muted-foreground font-light leading-relaxed">
          <p>{lang === "da"
            ? "Ved siden af mit rådgivningsarbejde bygger og driver jeg selv flere projekter. Det holder mig skarp, tvinger mig til at teste nye idéer i praksis og giver mig en forretningsforståelse, som ren rådgivning aldrig kan."
            : "Alongside my consulting work, I build and run several projects myself. It keeps me sharp, forces me to test new ideas in practice and gives me a business understanding pure consulting never could."}</p>
          <p>{lang === "da"
            ? "Projekterne spænder fra linkbuilding og ecommerce til det kreative — og fungerer også som mit eget testmiljø, før nye strategier rammer kundearbejdet."
            : "The projects span from link building and ecommerce to the creative — and also serve as my own test environment before new strategies reach client work."}</p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-16">
        <Stagger className="grid gap-6 md:grid-cols-2">
          {projects.map((p) => {
            const c = p[lang] || p.da;
            const isExternal = p.external_only;
            const inner = (
              <>
                <div className="flex items-center justify-between">
                  <span className="label-mono text-primary">{c.title}</span>
                  <ArrowUpRight size={22} className="text-muted-foreground transition-all group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                <p className="mt-6 font-heading text-xl font-bold tracking-tight leading-snug">{c.tagline}</p>
                <span className="mt-6 inline-block text-sm text-muted-foreground">{isExternal ? t("visit") : t("read_more")}</span>
              </>
            );
            return (
              <StaggerItem key={p.slug}>
                {isExternal ? (
                  <a href={p.external_url} target="_blank" rel="noopener noreferrer" data-testid={`project-card-${p.slug}`}
                    className="card-hover group flex h-full flex-col rounded-xl border border-border bg-card p-8">{inner}</a>
                ) : (
                  <Link to={buildPath("project", lang, { slug: p.slug })} data-testid={`project-card-${p.slug}`}
                    className="card-hover group flex h-full flex-col rounded-xl border border-border bg-card p-8">{inner}</Link>
                )}
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>
      <LeadMagnet profile={profile} />
    </>
  );
}
